import React, {useEffect, useRef, useState} from 'react';
import {DataView} from "primereact/dataview";
import {useTranslate} from "../../../../system/useTranslate";
import {ProgressBar} from "primereact/progressbar";
import BoxHeader from "../../../components/box-header";
import {Button} from "primereact/button";
import {useMountEffect, useUpdateEffect} from "primereact/hooks";
import {useProject} from "../../../../models/project/use-project";
import {useGoalComment} from "../../../../models/goal-comment/use-goal-comment";
import Avatar from "../../../../system/components/avatar";
import {InputTextarea} from "primereact/inputtextarea";
import Icon from "../../../../system/components/icon";
import {useDispatch, useSelector} from "react-redux";
import {useGoal} from "../../../../models/goal/use-goal";
import {Dialog} from "primereact/dialog";
import {setWorkspaceMembership} from "../../../../system/app-slice";
import {useNavigate} from "react-router-dom";
import GoalsListDialog from "../../components/goals-list-dialog";
import ProjectsListDialog from "../../components/projects-list-dialog";
import {Dropdown} from "primereact/dropdown";
import {timeSince} from "../../../../system/utils";
import DatatableEmptyMessage from "../../../../system/components/datatable-empty-message";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {OverlayPanel} from "primereact/overlaypanel";
import {ListBox} from "primereact/listbox";
import {MAX_ROOT_GOALS, MISSION_PARENT, goalAndDescendantIds, isActiveGoal, isRootGoal} from "../../components/goal-parent-select";
import EditableComment from "../../../components/editable-comment";
import GoalUpdatesList from "../../components/goal-updates-list";
import {usePermission} from "../../../../system/usePermission";
import {RichTextView} from "../../../../system/components/rich-text";
import {FormatToolbar, formatShortcut, formatTextBox} from "../../../inbox/text-formatting";
import {MessageContent} from "../../../inbox/chat-message";
import GoalProgressPanel from "../../progress/goal-progress-panel";

function Identification({goalData}) {
    const translate = useTranslate();
    return (
        <div className="flex h-full w-full">
            <div className="flex-1 flex flex-column p-3 overflow-y-auto scrollbar-hide">
                <QuickDetails goalData={goalData}/>
                {/* completion, current value, latest status and the progress chart (docs/tasks/28-goal-progress-source.md) */}
                <div className="p-2">
                    <GoalProgressPanel goalData={goalData}/>
                </div>
                <LinkedGoals goalData={goalData}/>
                <LinkedProjects goalData={goalData}/>
                <div className="p-2">
                    <CardView className="p-3 flex flex-column gap-2">
                        <BoxHeader title={translate("goal-description")}/>
                        <RichTextView value={goalData?.description}/>
                    </CardView>
                </div>
                <Comments goalData={goalData}/>
            </div>
            <div className="w-3 p-3 overflow-auto h-full scrollbar-hide">
                <GoalUpdatesList goalData={goalData}/>
            </div>
        </div>
    );
}

function QuickDetails({goalData}) {
    const translate = useTranslate();
    const goal = useGoal();
    const ItemTemplate = (data) => {

        return (
            <div className="p-2 lg:flex-1 md:w-6 w-full h-6rem">
                <CardView className={data.className}>
                    {data?.render}
                </CardView>
            </div>
        )
    }
    const cards = [
        {
            className: "flex align-items-center justify-content-center",
            render: (
                <div className="flex flex-column align-items-center justify-content-center">
                    <span className="text-400">{translate("goal-owner")}</span>
                    <span className="font-bold p-2">{goalData?.owner?.member?.fullname}</span>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render: (
                <div className="flex flex-column align-items-center justify-content-center">
                    <span className="text-400">{translate("team")}</span>
                    <span className="font-bold p-2">{translate(goalData?.team?.name)}</span>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render: (
                <div className="flex flex-column align-items-center justify-content-center">
                    <span className="text-400">{translate("cycle")}</span>
                    <span
                        className="font-bold p-2 text-center">{goalData?.cycle ? `${translate(goalData?.cycle?.range)} ${goalData?.cycle?.year}` : "---"}</span>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render: (
                <div className="flex flex-column align-items-center justify-content-center gap-2">
                    <span className="text-400">{translate("goal-status")}</span>
                    <span
                        className={`font-bold ${goal.statusClassName(goalData?.goalStatus, true, true)}`}>{translate(goalData?.goalStatus)}</span>
                </div>
            )

        },
        {
            className: "bg-orange-500 text-white flex align-items-center justify-content-center",
            render: (
                <div className="flex flex-column align-items-center justify-content-center">
                    <span>{translate("remain-days")}</span>
                    <span
                        className="text-lg font-bold p-2">{goalData?.dueDate ? timeSince(new Date(goalData?.dueDate).getTime() / 1000) : "---"}</span>
                </div>
            )
        }
    ]

    return (
        <DataView emptyMessage={<DatatableEmptyMessage/>} layout="grid" value={cards} itemTemplate={ItemTemplate}/>
    )
}

function CardView({children, className}) {
    return (
        <div className={`${className} h-full text-xs w-full shadow-1 border-round-xl`}>
            {children}
        </div>
    )
}

function LinkedProjects({goalData}) {
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const [projectsDialogVisible, setProjectsDialogVisible] = useState(false);
    const translate = useTranslate();
    const [projectsStats, setProjectsStats] = useState([])
    const goal = useGoal();
    const project = useProject();
    const ref = useRef()
    const ItemTemplate = (data) => {
        return (
            <div className="w-full text-xs py-1">
                <div className="p-2 w-full border-round surface-100 flex justify-content-between align-items-center">
                    <div className="flex flex-column gap-1">
                        <span className="font-bold">{data.title}</span>
                        <span
                            className={goal.statusClassName(data.projectStatus, true, true)}>{translate(data.projectStatus)}</span>
                    </div>
                    <ProgressBar className="w-10rem h-1rem text-2xs" showValue={true} value={data.progress.value}/>
                </div>
            </div>
        )
    }

    useEffect(() => {
        if (goalData) {
            getLinkedProjects()
        }
    }, [goalData]);

    const updateGoal = async (goalData, updateData) => {
        let _data = await goal.updateGoal(goalData?.documentId, updateData);
        let _goals = [...workspaceMembershipData?.workspace.goals];
        let _goalIndex = _goals.findIndex(g => g.documentId === goalData.documentId);
        _goals[_goalIndex] = {...goalData, ..._data}
        dispatch(setWorkspaceMembership({
            ...workspaceMembershipData,
            workspace: {...workspaceMembershipData.workspace, goals: _goals}
        }));
    }


    const getLinkedProjects = async () => {
        let _data = await project.getProjectsProgress({goals: {$in: [goalData?.id]}});
        setProjectsStats(_data);
    }

    return (
        <div className="p-2">
            <CardView className="p-3 flex flex-column gap-2">
                <BoxHeader title={translate("goal-projects-progress")} left={<Button onClick={(e) => {
                   ref.current.toggle(e)
                }} label={translate("add-projects-goal")} className="text-xs px-2 p-1"/>}/>
                <DataView emptyMessage={<span className="text-xs text-300">{translate("no-project-linked")}</span>}
                          itemTemplate={ItemTemplate} layout="list" value={projectsStats}/>
            </CardView>
            <OverlayPanel pt={{root: () => ({className: "p-0 rtl"}), content: () => ({className: "p-0"})}} ref={ref}>

                <SelectProjects value={goalData?.projects || []} onChange={(e) => {
                    updateGoal(goalData, {
                        projects: {
                            set: e.map(project => project.documentId || project.id),
                        },
                    });
                    // ref.current.hide()
                }}/>
            </OverlayPanel>

            {/*<ProjectsListDialog value={projectsStats} visible={projectsDialogVisible} onHide={()=>{setProjectsDialogVisible(false)}} onSelected={(e)=>{updateGoal(goalData, e.updatedData)}}/>*/}
        </div>
    )
}

function LinkedGoals({goalData}) {
    const [goalsDialogVisible, setGoalsDialogVisible] = useState(false);
    const translate = useTranslate();
    const goal = useGoal();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ref = useRef()
    const ItemTemplate = (data) => {
        return (
            <div className="w-full text-xs py-1">
                <div className="p-2 w-full border-round surface-100 flex justify-content-between align-items-center">
                    <div className="flex flex-column">
                        <span>{data?.title}</span>
                    </div>
                    <Button onClick={() => {
                        navigate(`/goals/${data.id}`)
                    }} pt={{label: () => ({className: "font-normal text-2xs"})}}
                            className="text-xs w-2 border-1 p-button-info p-button-outlined px-2 p-1"
                            label={translate("show-goal")}/>
                </div>
            </div>
        )
    }

    const updateGoal = async (goalData, updateData) => {
        let _data = await goal.updateGoal(goalData?.documentId, updateData);
        let _goals = [...workspaceMembershipData?.workspace.goals];
        let _goalIndex = _goals.findIndex(g => g.documentId === goalData.documentId);
        _goals[_goalIndex] = {...goalData, ..._data}

        dispatch(setWorkspaceMembership({
            ...workspaceMembershipData,
            workspace: {...workspaceMembershipData.workspace, goals: _goals}
        }));

    }

    return (
        <div className="p-2">
            <CardView className="p-3 flex flex-column gap-2">
                <BoxHeader title={translate("goal-links")}
                           left={<Button label={translate("link-to-other-goal")} className="text-xs px-2 p-1"
                                         onClick={(e) => {
                                             ref.current.toggle(e)
                                         }}/>}/>
                <DataView emptyMessage={<span className="text-xs text-300">{translate("goal-no-link")}</span>}
                          itemTemplate={ItemTemplate} layout="list"
                          value={goalData?.parentGoal ? [goalData?.parentGoal] : []}/>
                {isRootGoal(goalData) && (
                    <div className="w-full text-xs py-1">
                        <div className="p-2 w-full border-round flex align-items-center gap-2 font-bold" style={{background: "rgba(230, 81, 28, 0.08)", color: "#E6511C"}}>
                            <i className="pi pi-flag-fill text-xs"/>
                            {translate("goal-parent-mission")}
                        </div>
                    </div>
                )}
            </CardView>
            <OverlayPanel pt={{root: () => ({className: "p-0 rtl"}), content: () => ({className: "p-0"})}} ref={ref}>
                <SelectGoals goalData={goalData} onChange={(e) => {
                    // "mission" attaches the goal directly to the organization mission
                    updateGoal(goalData, e.isMission
                        ? {parentGoal: null, isOrphan: false}
                        : {parentGoal: e.id, isOrphan: false});
                    ref.current.hide()
                }}/>
            </OverlayPanel>

            {/*<GoalsListDialog goalData={goalData} value={goalData?.parentGoal} onSelected={(e)=>{updateGoal(goalData, e.updateData); setGoalsDialogVisible(false);}} visible={goalsDialogVisible} onHide={()=>{setGoalsDialogVisible(false)}}/>*/}
        </div>
    )
}

function Comments({goalData}) {
    const translate = useTranslate();
    const [comments, setComments] = useState([])
    const [error, setError] = useState("");
    const goalComment = useGoalComment();
    const permission = usePermission();
    const ItemTemplate = (data) => {
        return (
            <EditableComment
                comment={data}
                canManageOthers={permission.can("create-new-goals")}
                onError={setError}
                onUpdate={async (comment, text) => {
                    const updated = await goalComment.updateGoalComment(comment.documentId || comment.id, {text});
                    setComments((current) => current.map((item) => (
                        (item.documentId || item.id) === (comment.documentId || comment.id) ? updated : item
                    )));
                }}
                onDelete={async (comment) => {
                    await goalComment.deleteGoalComment(comment.documentId || comment.id);
                    setComments((current) => current.filter((item) => (
                        (item.documentId || item.id) !== (comment.documentId || comment.id)
                    )));
                }}
            >
                <div className="p-2 w-full border-round surface-50">
                    <MessageContent content={data.text}/>
                </div>
            </EditableComment>
        )
    }

    useEffect(() => {
        if (goalData) {
            getGoalComments()
        }
    }, [goalData]);

    const getGoalComments = async () => {
        try {
            setError("");
            const _data = await goalComment.getGoalComments(goalData.id);
            setComments(_data || []);
        } catch (e) {
            setError(translate("entity-comment.error.load"));
        }
    }

    return (
        <div className="p-2">
            <CardView className="p-3 flex flex-column gap-3">
                <BoxHeader title={translate("status-reports")}/>
                <CreateGoalComment goalData={goalData} onSubmit={(e) => {
                    setComments([...comments, e])
                }}/>
                {error && <span role="alert" className="text-xs text-red-500">{error}</span>}
                <DataView emptyMessage={<></>} itemTemplate={ItemTemplate} layout="list" value={comments}/>
            </CardView>
        </div>
    )
}

function SelectGoals({goalData, onChange}) {
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const translate = useTranslate();
    const goals = workspaceMembershipData?.workspace?.goals || [];

    // Not the goal itself nor anything below it (that would create a cycle).
    const blocked = goalAndDescendantIds(goals, goalData?.id);
    const parentId = goalData?.parentGoal?.id;
    const alreadyMissionGoal = isRootGoal(goalData);
    // The mission accepts at most MAX_ROOT_GOALS goals; hide it when it is full or this goal is already attached.
    const missionHasRoom = goals.filter((item) => isActiveGoal(item) && isRootGoal(item)).length < MAX_ROOT_GOALS;
    const options = [
        ...(!alreadyMissionGoal && missionHasRoom ? [{id: MISSION_PARENT, isMission: true, title: translate("goal-parent-mission")}] : []),
        ...goals.filter((item) => isActiveGoal(item) && !blocked.has(String(item.id)) && String(item.id) !== String(parentId)),
    ];

    const ItemTemplate = (data) => data?.isMission ? (
        <div className="w-full flex align-items-center gap-2 text-xs font-bold" style={{color: "#E6511C"}}>
            <i className="pi pi-flag-fill text-xs"/>
            {data.title}
        </div>
    ) : (
        <div className="w-fit flex align-items-center gap-1 text-xs">
            {data?.title}
        </div>
    );

    return (
        <div>
            <ListBox className="rtl border-none"
                     pt={{header: () => ({className: "p-2"})}}
                     options={options}
                     filterTemplate={(options) => (<InputText placeholder={translate("search-goals-placeholder")}
                                                              onChange={options.filterOptions.filter}
                                                              className="p-2 w-full text-xs"
                     />)}
                     filter filterBy="title"
                     itemTemplate={ItemTemplate}
                     onChange={(e) => {
                         if (e.value) onChange?.(e.value);
                     }}
                     value={null}
            />

        </div>
        // <div className="flex flex-column gap-2" >
        //     <OverlayPanel pt={{root:()=>({className:"p-0 rtl"}),content:()=>({className:"p-0"})}} ref={memberRef}>
        //         <MultiSelectMembers onChange={(e)=>{modifyMembers(e)}} options={workspaceMembershipData?.workspace?.workspace_memberships} value={newMembersData||[]}/>
        //     </OverlayPanel>
        //     <div className="border-round border-1 border-300 p-2 flex justify-content-between">
        //         <AvatarGroup className="ltr">
        //             {newMembersData?.map(m=>(<Avatar memberData={m.member}/>))}
        //         </AvatarGroup>
        //         <Icon icon="pi pi-chevron-down text-sm p-2" className="p-2" onClick={(e)=>{memberRef.current.toggle(e)}} />
        //     </div>
        // </div>
    )
}

function SelectProjects({value, onChange}) {
    const {config, workspaceMembershipData} = useSelector((state) => state.app);
    const translate = useTranslate();
    const [newGoalsData, setNewGoalsData] = useState();


    useEffect(() => {
        setNewGoalsData(value);
    }, []);


    useUpdateEffect(() => {
        if (newGoalsData) {
            onChange?.(newGoalsData);
        }
    }, [newGoalsData])


    //
    // const modifyMembers = (data)=>{
    //     if (data?.status==="added"){
    //         setNewMembersData([...newMembersData??[], data.member])
    //     }
    //     if (data?.status==="removed"){
    //         setNewMembersData([...newMembersData??[]].filter(m=>m.id!==data.member.id))
    //     }
    // }
    const ItemTemplate = (data) => {
        return data ? (
            <div className="w-fit flex align-items-center gap-1 text-xs">
                {data?.title}
            </div>
        ) : (
            <div className="p-2">{translate("select-projects-placeholder")}</div>
        )
    }
    const SelectedItemTemplate = (data) => {
        return (
            <div className="w-fit flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl">
                {/*<Avatar  memberData={data?.member}/>*/}
                {data?.title}
            </div>
        )
    }

    return (
        <div>
            {/*<MultiSelect selectedItemTemplate={SelectedItemTemplate}*/}
            {/*             inline*/}
            {/*             filterTemplate={(options)=>(<InputText placeholder={translate("search-goals-placeholder")} onChange={options.filterOptions.filter} className="p-2 w-full text-xs"/>)}*/}
            {/*             filter filterBy="title"*/}
            {/*             placeholder={translate("select-goals-placeholder")}*/}
            {/*             pt={{root:()=>({className:"w-full border-none shadow-none"}),label:()=>({className:"flex gap-1"}) ,*/}
            {/*                 labelContainer:()=>({className:"align-items-center flex pr-2"}) ,trigger:()=>({className:"p-3"}) ,*/}
            {/*                 filterInput:()=>({className:"p-0"}), header:()=>({className:"p-2 flex-row-reverse"}),*/}
            {/*                 item:()=>({className:"flex-row-reverse justify-content-between p-2"})}} panelClassName="rtl" onChange={(e)=>{setNewGoalsData(e.selectedOption)}}*/}
            {/*             itemTemplate={ItemTemplate}*/}
            {/*             options={workspaceMembershipData?.workspace?.goals}*/}
            {/*             value={[newGoalsData]||[]}/>*/}

            <ListBox multiple className="rtl border-none" pt={{header: () => ({className: "p-2"})}}
                     options={workspaceMembershipData?.workspace?.projects}                     filterTemplate={(options) => (<InputText placeholder={translate("search-goals-placeholder")}
                                                              onChange={options.filterOptions.filter}
                                                              className="p-2 w-full text-xs"/>)}
                     filter filterBy="title"
                     itemTemplate={ItemTemplate}
                     onChange={(e) => {
                         setNewGoalsData(e.value)
                     }}
                     value={newGoalsData}


            />

        </div>
        // <div className="flex flex-column gap-2" >
        //     <OverlayPanel pt={{root:()=>({className:"p-0 rtl"}),content:()=>({className:"p-0"})}} ref={memberRef}>
        //         <MultiSelectMembers onChange={(e)=>{modifyMembers(e)}} options={workspaceMembershipData?.workspace?.workspace_memberships} value={newMembersData||[]}/>
        //     </OverlayPanel>
        //     <div className="border-round border-1 border-300 p-2 flex justify-content-between">
        //         <AvatarGroup className="ltr">
        //             {newMembersData?.map(m=>(<Avatar memberData={m.member}/>))}
        //         </AvatarGroup>
        //         <Icon icon="pi pi-chevron-down text-sm p-2" className="p-2" onClick={(e)=>{memberRef.current.toggle(e)}} />
        //     </div>
        // </div>
    )
}


function CreateGoalComment({goalData, onSubmit}) {
    const {user} = useSelector(state => state.auth);
    const translate = useTranslate();
    const [text, setText] = useState();
    const inputRef = useRef(null);
    const goalComment = useGoalComment();

    const createComment = async () => {
        let _data = await goalComment.createGoalComment({goal: goalData.id, text});
        onSubmit?.(_data);
        setText("");
    }

    return (
        <div className="w-full flex gap-2">
            <Avatar memberData={user}/>
            <div className="flex-1 border-1 border-round-3xl border-300 p-2">
                <InputTextarea ref={inputRef} value={text || ""} onChange={(e) => {
                    setText(e.target.value)
                }} onKeyDown={(event) => {
                    const kind = formatShortcut(event);
                    if (kind) {
                        event.preventDefault();
                        formatTextBox(inputRef.current, text, kind, setText);
                    }
                }} placeholder={translate("your-comment-placeholder")}
                               className="w-full border-none shadow-none p-1 text-sm" rows={3} autoResize/>
                <FormatToolbar className="px-1 pb-1" onFormat={(kind) => formatTextBox(inputRef.current, text, kind, setText)}/>
                <div className="flex justify-content-between align-items-center">
                    <div className="flex align-items-center gap-3 text-500">
                        <Icon icon="mi mi:alternate_email" onClick={() => {
                        }}/>
                        <Icon icon="mi mi:sentiment_satisfied" onClick={() => {
                        }}/>
                        <Icon icon="mi mi:attach_file" onClick={() => {
                        }}/>
                    </div>
                    <Button className="p-2 pl-3 pr-3 text-xs font-light border-round-2xl" label={translate("send")}
                            onClick={() => {
                                createComment()
                            }}/>
                </div>
            </div>

        </div>
    )
}

export default Identification;
